import {
  Callout,
  GuideFlow,
  GuidePage,
  GuideSection,
  GuideSteps,
  GuideSubsection,
  GuideTable,
  GuideToc,
  Ui,
} from './GuideParts';

/**
 * The Supplier Portal Operating Guide, at /supplier/guide.
 *
 * Written for the factory, not for us. Two rules held throughout:
 *
 * Every label quoted here is the label the supplier actually sees, which is
 * not always the one WE see. The slot TAP reads as needing a revision reads to
 * the supplier as "New samples needed"; documenting our vocabulary would send
 * somebody hunting the screen for words that are not on it. (guideContent.test
 * asserts the internal labels are absent by scanning this file as text, so the
 * comment says it the long way round rather than quoting the label it bans.)
 *
 * And nothing internal appears — no client tags, no other factory's name, no
 * pricing, no review mechanics beyond what lands in their portal. This page is
 * inside the supplier shell, so a supplier can read it; anything on it should
 * be safe in a competitor's hands.
 */

const TOC = [
  { id: 'signing-in', label: 'Signing in' },
  { id: 'layout', label: 'Your portal, tab by tab' },
  { id: 'orders', label: 'Accepting an order, and the sample clock' },
  { id: 'handoffs', label: 'Designs from TAP, and acknowledging them' },
  { id: 'samples', label: 'Sending samples: the four components' },
  { id: 'revisions', label: 'When TAP asks for new samples' },
  { id: 'rules', label: 'Limits, locks and refusals' },
  { id: 'stock', label: 'Materials stock reports' },
  { id: 'messages', label: 'Reviews, files and messages' },
  { id: 'cannot', label: 'What you cannot do, and who to ask' },
];

export function SupplierGuidePage() {
  return (
    <GuidePage
      eyebrow="4ocean · Supplier Portal"
      title="Supplier Operating Guide"
      intro="How to work with TAP through the portal: acknowledging designs, sending pre-production samples, and responding when something needs re-shooting."
    >
      <GuideToc entries={TOC} />

      <GuideSection id="signing-in" title="1. Signing in">
        <GuideSteps
          steps={[
            <>
              Go to <strong>tap-creative-operations-hub.pages.dev</strong> and click{' '}
              <Ui>Sign in with Google</Ui>. Use the exact email address TAP invited — a different
              address, even at the same company, will not be recognised.
            </>,
            <>
              You land on <Ui>Supplier Portal</Ui>. The sidebar says <Ui>4ocean Supplier</Ui> with{' '}
              <Ui>Portal</Ui> at the top, and <Ui>📖 Supplier Guide</Ui> pinned at the bottom — that
              is this page, and it is always one click away from wherever you are.
            </>,
            <>
              Your own email address is shown at the top right. Clicking it opens{' '}
              <Ui>Connect Google Drive</Ui> and <Ui>Sign out</Ui>. The four small icons beside it
              switch the colour theme — <Ui>Light</Ui>, <Ui>Dusk</Ui>, <Ui>Dark</Ui> and{' '}
              <Ui>Matcha</Ui> — and nothing else.
            </>,
          ]}
        />
        <Callout>
          If you see <Ui>Supplier access is not available</Ui>, your organisation has been set
          inactive rather than your login being wrong. Contact your TAP representative — signing in
          again will not clear it.
        </Callout>
      </GuideSection>

      <GuideSection id="layout" title="2. Your portal, tab by tab">
        <p>
          Everything is on one page, behind a row of tabs. They are always in this order, and{' '}
          <Ui>Action Required</Ui> carries a number when there is something waiting on you.
        </p>
        <GuideTable
          headers={['Tab', 'What is in it']}
          rows={[
            [
              <Ui key="a">Action Required</Ui>,
              'Everything waiting on you, gathered from the other tabs: submissions TAP has sent back, designs you have not acknowledged, and reviews needing a response. When it is empty it reads "Nothing needs your action right now". Start here every time.',
            ],
            [
              <Ui key="a2">Active Orders</Ui>,
              'Production orders TAP has issued to you. Accept them here, or ask for a change. Accepting is what opens the sample slots for that order.',
            ],
            [
              <Ui key="b">Send Samples</Ui>,
              'Your materials stock report, and the sample submissions you send TAP for approval. This is where photo uploads happen.',
            ],
            [
              <Ui key="c">From TAP</Ui>,
              'Final client-approved designs sent to you for acknowledgement, with their notes, tags and file links.',
            ],
            [
              <Ui key="d">Shared Reviews</Ui>,
              'Task-level reviews TAP has shared with you, each with its own instructions, files, sample versions and conversation.',
            ],
            [
              <Ui key="e">Files for Review</Ui>,
              'A flat list of files shared with you, newest first, each showing when it was shared.',
            ],
            [
              <Ui key="f">Messages</Ui>,
              'A reminder that conversations live inside each review, plus the same review list. There is no separate inbox.',
            ],
            [
              <Ui key="g">Completed</Ui>,
              'Finished work: sample runs approved for production, and reviews that reached the same point. Nothing here needs anything from you.',
            ],
          ]}
        />
      </GuideSection>

      <GuideSection id="orders" title="3. Accepting an order, and the sample clock">
        <p>
          Once TAP issues a production order to you it appears under <Ui>Active Orders</Ui>, marked{' '}
          <Ui>Awaiting your confirmation</Ui>. The card shows what to make — units, product, SKU and
          spec per line — the <Ui>Ship to</Ui> block, and the target ship date.
        </p>
        <Callout>
          There are no prices anywhere on it, and that is deliberate rather than an oversight. A
          production order tells you what to build and where to send it. Anything about what the end
          client paid is not part of your job and is never sent to the portal.
        </Callout>

        <GuideSubsection title="Confirm, or ask for a change">
          <GuideTable
            headers={['Button', 'What it does']}
            rows={[
              [
                <Ui key="c">Confirm &amp; Accept Order</Ui>,
                'You are taking the job on. The order moves into production, your four sample slots open for it, and the sample clock starts.',
              ],
              [
                <Ui key="r">Request Revision</Ui>,
                'You need something changed first — a date that is not achievable, a quantity that changes the cost. Type what needs changing and send. The order does not move, and TAP answers by editing it.',
              ],
            ]}
          />
          <Callout tone="warn">
            Confirming is one-way and starts a clock, so read the lines and the date before you
            click. If the date is not possible, use <Ui>Request Revision</Ui> first — that is what
            it is for, and it is much cheaper than discovering it a week in.
          </Callout>
        </GuideSubsection>

        <GuideSubsection title="Accepting opens the sample slots">
          <p>
            You do not need to start a submission by hand for a confirmed order. Accepting creates
            it for you, so the four components — <strong>Charm</strong>,{' '}
            <strong>Bracelet Cord</strong>, <strong>Packaging Front and Back</strong> and{' '}
            <strong>Impact Card Front and Back</strong> — are immediately open for that order under{' '}
            <Ui>Send Samples</Ui>. Upload into them exactly as described below.
          </p>
        </GuideSubsection>

        <GuideSubsection title="The sample clock">
          <p>
            Confirming starts a countdown for sending your samples. It runs from{' '}
            <em>your confirmation</em>, not from when TAP raised the order — you are never charged
            for the days it took to reach you. The standard window is <strong>21 days</strong>; a
            rush order may be shorter, and the badge always shows the real figure.
          </p>
          <GuideTable
            headers={['Badge', 'What it means']}
            rows={[
              [
                <Ui key="g">🟢 14 days remaining</Ui>,
                'On track. The number counts whole days left.',
              ],
              [
                <Ui key="y">🟡 Under 24 hours (5h)</Ui>,
                'The last day. It counts down in hours, rounded down — 5h means five hours, not nearly six.',
              ],
              [
                <Ui key="r2">🔴 SLA overdue by 3 days</Ui>,
                'Past the window. Tell TAP where the samples are rather than waiting for them to ask.',
              ],
            ]}
          />
          <Callout>
            TAP sees exactly the same badge on their side, from the same clock. There is no separate
            internal number, and nothing to reconcile — if it is amber for you it is amber for them.
          </Callout>
        </GuideSubsection>
        <GuideFlow
          label="An order, end to end"
          steps={[
            'Awaiting your confirmation',
            'Accepted, clock starts',
            'Slots open',
            'Samples sent',
          ]}
        />
      </GuideSection>

      <GuideSection id="handoffs" title="4. Designs from TAP, and acknowledging them">
        <p>
          A handoff is a final design the client has already approved. Acknowledging it tells TAP
          you have seen it and production can be planned around it — it is not an invoice, an order
          or a commitment to a date.
        </p>
        <GuideSteps
          steps={[
            <>
              Open <Ui>From TAP</Ui>. Anything outstanding shows the badge{' '}
              <Ui>Needs acknowledgement</Ui>.
            </>,
            <>
              Read the notes and open every link. One link is often a whole Drive folder rather than
              a single file, so check inside before assuming you have everything.
            </>,
            <>
              If anything needs raising — a material you cannot source, a size that will not hold —
              type it into <Ui>Note to TAP (optional)</Ui> <em>before</em> you acknowledge. This is
              the moment it is cheapest to fix.
            </>,
            <>
              Click <Ui>Acknowledge</Ui>. The badge switches to <Ui>Acknowledged</Ui>, the button
              disappears, and your note is shown afterwards as <Ui>Your note: …</Ui>.
            </>,
          ]}
        />
        <Callout tone="warn">
          Acknowledging is one-way. There is no un-acknowledge button, and trying again returns
          &ldquo;This handoff was already acknowledged.&rdquo; If you acknowledged something by
          mistake, say so in the review conversation rather than looking for an undo.
        </Callout>
        <p>
          You cannot edit the handoff itself — its title, notes, tags and links belong to TAP. If
          you try, you will see &ldquo;Only TAP can edit the handoff itself — add your note when
          acknowledging.&rdquo; Your note is your channel.
        </p>
      </GuideSection>

      <GuideSection id="samples" title="5. Sending samples: the four components">
        <p>
          A sample submission is judged in four separate parts. Each is approved on its own, which
          is what stops you re-shooting a charm that was signed off two weeks ago because the
          packaging was wrong.
        </p>
        <GuideTable
          caption="The four components, always in this order"
          headers={['Component', 'What TAP is looking at']}
          rows={[
            ['Charm', 'The metal piece itself — finish, plating, weight, engraving.'],
            ['Bracelet Cord', 'Cord colour, weave, thickness, and how it terminates.'],
            [
              'Packaging Front and Back',
              'Both sides of the packaging, kept together as one component. Upload packaging evidence here, separate from the impact card.',
            ],
            [
              'Impact Card Front and Back',
              'Both sides of the impact card, kept together as one component. Upload impact-card evidence here, separate from the packaging.',
            ],
          ]}
        />
        <p>
          For production artwork, keep the front and back of each product together in that
          product&apos;s file. For sample photos, upload each product under its matching component.
          Do not combine the packaging front with the impact card front in one file, or combine
          their backs in another. Existing orders keep their original slot names and approved files.
          Follow the labels and artwork shown on that order, and ask TAP before regrouping existing
          artwork.
        </p>

        <GuideSubsection title="Starting a submission">
          <GuideSteps
            steps={[
              <>
                Open <Ui>Send Samples</Ui> and click <Ui>New submission</Ui>.
              </>,
              <>
                Fill in <Ui>What are you sending?</Ui> — be specific enough that TAP can tell two
                runs apart months later. The placeholder shows the shape they expect:{' '}
                <em>Mesh Bag v2 — pre-production samples</em>.
              </>,
              <>
                Add anything TAP should know in <Ui>Notes for TAP</Ui>, then click{' '}
                <Ui>Send to TAP</Ui>. The submission is created empty — photos come next, and the
                form says so: <em>You can upload the sample photos right after sending.</em>
              </>,
            ]}
          />
        </GuideSubsection>

        <GuideSubsection title="Uploading photos into a component">
          <p>
            Each component has its own upload button, and it is numbered:{' '}
            <Ui>+ Upload Option 1 Photo</Ui>, then <Ui>+ Upload Option 2 Photo</Ui>, and so on. It
            reads <Ui>Uploading…</Ui> while the photo is going up.
          </p>
          <Callout>
            The numbering is the instruction. TAP <em>chooses between</em> options, so a component
            with one photo gives them nothing to choose from — send two or three real alternatives
            per component (different angle, different finish, different print) and the decision
            comes back faster. The count restarts at Option 1 each time a component is sent back for
            a new round.
          </Callout>
          <p>
            A component only accepts photos in two states. If the control has been replaced by a
            line of text, that text says why:
          </p>
          <GuideTable
            headers={['What you see on the component', 'What it means', 'Can you upload?']}
            rows={[
              [
                <Ui key="a">Not sent yet</Ui>,
                'Nothing has been uploaded for this component.',
                'Yes',
              ],
              [
                <Ui key="b">New samples needed</Ui>,
                'TAP turned down everything you sent for this component and wants another round.',
                'Yes — this is the one to act on',
              ],
              [
                <Ui key="c">With TAP</Ui>,
                'Your photos are in and TAP is deciding. The control is replaced by "Sent. TAP is reviewing these — no new photos needed yet."',
                'No',
              ],
              [
                <Ui key="d">Approved</Ui>,
                'One option was chosen. The control is replaced by "Approved and locked. Nothing more is needed for this component."',
                'No, and it will not reopen',
              ],
            ]}
          />
          <p>
            Each photo you upload carries its own small badge once TAP has looked at it:{' '}
            <Ui>Chosen</Ui>, <Ui>Not chosen</Ui>, or <Ui>Pending</Ui> while they are still deciding.
            A component showing <Ui>Round 2</Ui> or higher is on a later attempt.
          </p>
        </GuideSubsection>

        <GuideFlow
          label="The life of one component"
          steps={['Not sent yet', 'With TAP', 'New samples needed', 'Approved']}
        />

        <p>
          The submission as a whole also carries a status: <Ui>Awaiting TAP review</Ui>,{' '}
          <Ui>Revision requested</Ui>, <Ui>On hold</Ui>, or <Ui>Approved for production</Ui>. It is
          only finished when all four components are approved — a component with nothing uploaded
          holds the whole submission open even if the other three are signed off.
        </p>

        <GuideSubsection title="How the list is arranged">
          <p>
            <Ui>Send Samples</Ui> only shows runs that are still in play, in three collapsible
            groups: <Ui>Needs your action</Ui> first and open, then <Ui>With TAP</Ui>, then{' '}
            <Ui>On hold</Ui> which starts closed. A group with nothing in it is not shown at all.
          </p>
          <p>
            Once a run is <Ui>Approved for production</Ui> it leaves this tab entirely and moves to{' '}
            <Ui>Completed</Ui>. Nothing is deleted — it is finished, and keeping it here only made
            the run that still needed you harder to find.
          </p>
        </GuideSubsection>
      </GuideSection>

      <GuideSection id="revisions" title="6. When TAP asks for new samples">
        <p>
          A revision is targeted. When TAP sends a submission back, only the components that
          actually failed reopen; everything already approved stays locked and finished.
        </p>
        <GuideSteps
          steps={[
            <>
              The submission appears under <Ui>Action Required</Ui> with the badge{' '}
              <Ui>Revision requested</Ui> and the line{' '}
              <em>Revise and resubmit under Send Samples</em>.
            </>,
            <>
              Open it and look for the components reading <Ui>New samples needed</Ui>. Those are the
              only ones you need to re-shoot.
            </>,
            <>
              Read the feedback before you shoot anything. Under each reopened component is a
              disclosure headed <Ui>What TAP said</Ui> with a count — it is already open on a
              component needing revision. Inside, every rejected photo is listed as{' '}
              <em>photo label: what was wrong with it</em>.
            </>,
            <>
              The list carries across rounds, so notes from earlier attempts are still there. Read
              all of them — the fastest way to a third round is fixing round two&rsquo;s note while
              repeating round one&rsquo;s mistake.
            </>,
            <>
              Upload the new photos into that same component. There is no separate resubmit button:
              filling the reopened components is the resubmission.
            </>,
          ]}
        />
        <Callout>
          TAP also writes one overall note when they send a submission back. That note is required
          on their side for a revision, so if something is unclear, it is worth re-reading before
          asking.
        </Callout>
      </GuideSection>

      <GuideSection id="rules" title="7. Limits, locks and refusals">
        <p>
          A few rules are enforced by the system itself, not by whoever is watching. They apply to a
          second browser tab and a stale page exactly as they apply here, so if something is
          refused, this is usually why.
        </p>
        <GuideTable
          headers={['If you see', 'What happened', 'What to do']}
          rows={[
            [
              '“That component already holds five options for this round.”',
              'Five photos is the cap per component, per round. It exists so a decision is between a handful of real candidates rather than a contact sheet.',
              'Pick your five best. If none are right, wait for TAP to send it back — a new round gives you five more.',
            ],
            [
              '“That component is already approved and locked. Nothing more is needed for it.”',
              'One option in that component was chosen. An approved component never reopens.',
              'Nothing. Move to the components still asking for photos.',
            ],
            [
              '“This submission is closed. TAP has to send it back before more samples can be added.”',
              'The submission has been decided — approved, or put on hold — so it no longer accepts uploads.',
              'Ask TAP to reopen it, or start a new submission if it is genuinely a new run.',
            ],
            [
              '“Only the TAP team can rename a submission.”',
              'Titles belong to TAP once sent.',
              'Ask TAP, or note the correction in the conversation.',
            ],
          ]}
        />
        <Callout tone="warn">
          Round numbers are decided by the system, never by you or by TAP typing one in. A round
          stays open until every photo in it has been turned down; only then does a new one begin.
        </Callout>
      </GuideSection>

      <GuideSection id="stock" title="8. Materials stock reports">
        <p>
          At the top of <Ui>Send Samples</Ui> is <Ui>Materials stock</Ui> — what TAP believes you
          are holding. It reads <Ui>Stock as of …</Ui> with the date of the last report, or{' '}
          <em>No stock report for this supplier yet.</em>
        </p>
        <GuideSteps
          steps={[
            <>
              Click <Ui>Upload stock report</Ui> and drop your <strong>.xlsx</strong> onto{' '}
              <Ui>Drop the .xlsx here</Ui>, or use <Ui>or choose a file</Ui>.
            </>,
            <>
              Check the summary line before saving — it shows the report date and how many materials
              were read. If the file had no date in it, the date is assumed and flagged; a wrong
              month makes the whole comparison meaningless.
            </>,
            <>
              Click <Ui>Save snapshot</Ui>, or <Ui>Choose another file</Ui> if the numbers look
              wrong.
            </>,
          ]}
        />
        <p>
          Filter the table with <Ui>All materials</Ui>, <Ui>Spools</Ui>, <Ui>Beads</Ui> and{' '}
          <Ui>Charms</Ui>. Rows are marked <Ui>In stock</Ui>, <Ui>Low stock</Ui> or{' '}
          <Ui>Depleted</Ui>.
        </p>
        <Callout>
          Runway — how many days of supply you have left — cannot be worked out from a single
          report, because it needs two months to compare. Until then you will see{' '}
          <em>
            No burn rates yet — these need a second monthly report before runway can be worked out.
          </em>{' '}
          Sending one report a month, on a steady day, is what makes this useful.
        </Callout>
      </GuideSection>

      <GuideSection id="messages" title="9. Reviews, files and messages">
        <p>
          Opening a review from <Ui>Shared Reviews</Ui>, <Ui>Messages</Ui> or{' '}
          <Ui>Action Required</Ui> opens the same full-screen view: its status, due date,{' '}
          <Ui>Instructions from TAP</Ui>, <Ui>Design files shared with you</Ui>,{' '}
          <Ui>Sample versions</Ui>, and <Ui>Conversation with TAP</Ui> at the bottom.
        </p>
        <GuideSubsection title="Sample versions inside a review">
          <p>
            These are separate from the four-component submissions above. Click{' '}
            <Ui>Start a new sample version</Ui>, add a note if useful, then add photos with{' '}
            <Ui>Upload photo</Ui> or <Ui>Paste a link instead</Ui>. Nothing reaches TAP until you
            click <Ui>Submit for TAP Review</Ui> and then <Ui>Confirm submit</Ui>.
          </p>
          <Callout tone="warn">
            The confirmation says it plainly: submitting locks that version. Add every photo you
            mean to send before you submit, because you cannot add one afterwards — you would need a
            whole new version.
          </Callout>
        </GuideSubsection>
        <GuideSubsection title="Messages">
          <p>
            Type into <Ui>Message TAP</Ui> and click <Ui>Send</Ui>. You can <Ui>Edit</Ui> or{' '}
            <Ui>Delete</Ui> your own messages; an edited one is marked. There is no notification
            inbox in the portal, so check the reviews you have open rather than waiting to be
            pinged.
          </p>
        </GuideSubsection>
      </GuideSection>

      <GuideSection id="cannot" title="10. What you cannot do, and who to ask">
        <p>Worth knowing so you do not go looking for a button that is not there.</p>
        <GuideTable
          headers={['Not available to you', 'Why, and what to do instead']}
          rows={[
            [
              'Approving or rejecting your own submission',
              'Only a TAP admin decides. You will see the outcome as a status change and a note.',
            ],
            [
              'Renaming a submission, or editing a handoff',
              'Both belong to TAP once sent. Raise it in the conversation, or in your acknowledgement note.',
            ],
            [
              'Seeing which client a sample run is for',
              'End-client names are internal to TAP and never shown in the portal. This is deliberate and not a permissions fault.',
            ],
            [
              'Seeing any other supplier, or being switched between organisations',
              'Your portal only ever shows your own organisation. There is no picker.',
            ],
            [
              'Adding photos to a component while it reads “With TAP”',
              'The component is mid-decision. Wait for it to come back as approved or as “New samples needed”.',
            ],
            [
              'Prices, margins or what the client paid',
              'No document you can reach carries money. A production PO tells you what to make and where to send it, and nothing else.',
            ],
          ]}
        />
        <Callout>
          If something here does not match what you see on screen, trust the screen and tell your
          TAP contact — this guide is updated with the portal, and a mismatch means one of us is out
          of date.
        </Callout>
      </GuideSection>
    </GuidePage>
  );
}
